// CC-055/055b — Hiring settings: Call Prep switch, stages, and each stage's own question list.
import { useEffect, useState } from "react";
import { Loader2, Plus, Save, ShieldCheck, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const STAGES = [2, 3, 4, 5];
const errText = (e) => e?.response?.data?.detail || e.message;

function BaselineRow({ it, i, onChange, onRemove }) {
  return (
    <div className="border border-[#e5e7eb] rounded-md p-2 space-y-1" data-testid={`call-prep-baseline-row-${i}`}>
      <textarea rows={2} value={it.text} onChange={(e) => onChange({ ...it, text: e.target.value })}
                className="w-full rounded border border-[#d1d5db] px-2 py-1 text-sm" data-testid={`call-prep-baseline-text-${i}`} />
      <input value={it.script || ""} placeholder="Script line to read aloud (optional)"
             onChange={(e) => onChange({ ...it, script: e.target.value })}
             className="w-full rounded border border-[#d1d5db] px-2 py-1 text-xs" data-testid={`call-prep-baseline-script-${i}`} />
      <div className="flex items-center justify-between text-xs">
        <label className="inline-flex items-center gap-1">
          <input type="checkbox" checked={!!it.compliance} onChange={(e) => onChange({ ...it, compliance: e.target.checked })}
                 data-testid={`call-prep-baseline-compliance-${i}`} />
          <ShieldCheck className="w-3 h-3" /> Compliance item (shown word for word; only skipped if the application answers it exactly)
        </label>
        <button className="text-[#991b1b]" onClick={onRemove} data-testid={`call-prep-baseline-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function StageList({ items, setItems }) {
  return (
    <div className="space-y-2">
      {items.length === 0 && <div className="text-xs text-[#6b7280]" data-testid="call-prep-stage-list-empty">No questions for this stage.</div>}
      {items.map((it, i) => (
        <BaselineRow key={`${it.key}-${i}`} it={it} i={i}
                     onChange={(v) => setItems(items.map((x, j) => (j === i ? v : x)))}
                     onRemove={() => setItems(items.filter((_, j) => j !== i))} />
      ))}
      <button className="inline-flex items-center gap-1 text-xs text-[#0072bc]" data-testid="call-prep-baseline-add"
              onClick={() => setItems([...items, { key: "", text: "", compliance: false, script: "" }])}>
        <Plus className="w-3.5 h-3.5" /> Add question
      </button>
    </div>
  );
}

export default function CallPrepSettingsTab({ slug }) {
  const q = slug ? `?tenant_slug=${encodeURIComponent(slug)}` : "";
  const [cfg, setCfg] = useState(null);
  const [denied, setDenied] = useState(false);
  const [edit, setEdit] = useState(2);
  const [save, setSave] = useState({});
  useEffect(() => {
    api.get(`/hiring/settings/call-prep${q}`).then(({ data }) => setCfg(data))
      .catch((e) => (e?.response?.status === 403 ? setDenied(true) : setSave({ error: String(errText(e)) })));
  }, [q]);
  if (denied) return <div className="text-sm text-[#4b5563]" data-testid="call-prep-settings-denied">Only owners, platform admins and Call Prep editors can open this tab.</div>;
  if (!cfg) return <Loader2 className="w-4 h-4 animate-spin" data-testid="call-prep-settings-loading" />;
  const list = edit === 2 ? cfg.call_prep_baseline : cfg.call_prep_baselines[String(edit)] || [];
  const setList = (items) => setCfg((c) => (edit === 2 ? { ...c, call_prep_baseline: items }
    : { ...c, call_prep_baselines: { ...c.call_prep_baselines, [String(edit)]: items } }));
  const toggleStage = (s) => setCfg((c) => ({ ...c, call_prep_stages: c.call_prep_stages.includes(s)
    ? c.call_prep_stages.filter((x) => x !== s) : [...c.call_prep_stages, s].sort() }));
  const onSave = async () => {
    setSave({ saving: true });
    const body = { call_prep_enabled: cfg.call_prep_enabled, call_prep_stages: cfg.call_prep_stages,
      call_prep_baseline: cfg.call_prep_baseline, call_prep_baselines: cfg.call_prep_baselines, call_prep_notes: cfg.call_prep_notes };
    if (cfg.can_edit_editor_list) body.call_prep_editor_emails = cfg.call_prep_editor_emails;
    try { const { data } = await api.patch(`/hiring/settings/call-prep${q}`, body); setCfg(data); setSave({ saved: true }); }
    catch (e) { setSave({ error: String(errText(e)) }); }
  };
  return (
    <div className="space-y-5 max-w-2xl" data-testid="hiring-call-prep-tab">
      <label className="flex items-center gap-2 text-sm font-medium text-[#0e1420]">
        <input type="checkbox" checked={!!cfg.call_prep_enabled} data-testid="call-prep-enabled-toggle"
               onChange={(e) => setCfg((c) => ({ ...c, call_prep_enabled: e.target.checked }))} />
        Show Call Prep to the stage owner
      </label>
      <div className="text-sm" data-testid="call-prep-stages">
        Show at stages:
        {STAGES.map((s) => (
          <label key={s} className="ml-3 inline-flex items-center gap-1">
            <input type="checkbox" checked={cfg.call_prep_stages.includes(s)} onChange={() => toggleStage(s)} data-testid={`call-prep-stage-${s}`} /> {s}
          </label>
        ))}
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-[#0e1420]">Questions for stage</h2>
          {STAGES.map((s) => (
            <button key={s} onClick={() => setEdit(s)} data-testid={`call-prep-edit-stage-${s}`}
                    className={`px-2 py-0.5 rounded text-xs border ${edit === s ? "bg-[#0e1420] text-white" : "border-[#d1d5db]"}`}>{s}</button>
          ))}
        </div>
        <input value={cfg.call_prep_notes[String(edit)] || ""} placeholder="Banner shown above this stage's list (optional)"
               onChange={(e) => setCfg((c) => ({ ...c, call_prep_notes: { ...c.call_prep_notes, [String(edit)]: e.target.value } }))}
               className="w-full rounded border border-[#d1d5db] px-2 py-1 text-xs" data-testid="call-prep-stage-note-input" />
        <StageList items={list} setItems={setList} />
      </div>
      {cfg.can_edit_editor_list && (
        <label className="block text-xs">Call Prep editors (emails that may edit only this tab, comma separated)
          <input value={(cfg.call_prep_editor_emails || []).join(", ")} data-testid="call-prep-editor-emails-input"
                 onChange={(e) => setCfg((c) => ({ ...c, call_prep_editor_emails: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))}
                 className="w-full rounded border border-[#d1d5db] px-2 py-1 text-xs" />
        </label>
      )}
      {save.error && <div className="text-xs text-[#991b1b]" data-testid="call-prep-settings-error">{save.error}</div>}
      {save.saved && <div className="text-xs text-[#16a34a]" data-testid="call-prep-settings-saved">Saved.</div>}
      <div className="flex justify-end">
        <Button data-testid="call-prep-settings-save" disabled={save.saving || cfg.call_prep_stages.length === 0} onClick={onSave}>
          {save.saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}
